import { clear, el, on } from './dom.js';
import { icon } from './icons.js';

/**
 * A walk round the screen, pointing at the real thing.
 *
 * The interface was learnable by experiment, which is another way of saying
 * somebody had to decipher it. Hints along the bottom helped but arrived one
 * at a time and in no particular order, and a player who wanted to know what
 * a button did had to press it and find out.
 *
 * So this points at the actual element — the rack, the shuffle button, the
 * call button — dims everything else, and says in a sentence what it is for.
 * It reads the element's position from the live layout rather than being told
 * where things are, so it cannot go stale when the layout changes, and a step
 * whose target is missing on this screen is skipped rather than pointing at
 * nothing.
 */

export interface TourStep {
  /** The element to point at. Returned lazily: panels open as we go. */
  target?: () => Element | null | undefined;
  title: string;
  body: string;
  /** Run before the step is measured — opening a panel, say. */
  before?: () => void;
  /** Run when the step is left. */
  after?: () => void;
}

export interface TourHandle { stop(): void }

const RING = 8;         // breathing room around the highlighted element
const GAP = 14;         // between the ring and the callout

export function runTour(
  steps: readonly TourStep[],
  opts: { onDone?: (finished: boolean) => void; label?: string } = {},
): TourHandle {
  const live = steps.filter((s) => !s.target || true);   // resolved per step
  if (live.length === 0) {
    opts.onDone?.(false);
    return { stop: () => {} };
  }

  const root = el('div', { class: 'tour', role: 'dialog', 'aria-modal': 'true' });
  // Four panes dimming everything outside the hole. Four divs rather than a
  // mask: `mask-composite` and `clip-path` on a full-screen overlay are the
  // two things most likely to differ between engines, and this cannot.
  const panes = ['t', 'r', 'b', 'l'].map((side) =>
    el('div', { class: `tour__dim tour__dim--${side}` }));
  const ring = el('div', { class: 'tour__ring' });
  const card = el('div', { class: 'tour__card' });
  const title = el('h3', { class: 'tour__title' });
  const body = el('p', { class: 'tour__body' });
  const dots = el('div', { class: 'tour__dots' });
  const back = el('button', { class: 'btn btn--sm btn--ghost', type: 'button', text: 'Back' });
  const next = el('button', { class: 'btn btn--sm btn--primary', type: 'button', text: 'Next' });
  const skip = el('button', {
    class: 'btn btn--sm btn--ghost', type: 'button', text: 'Close',
    'aria-label': 'Close the walkthrough',
  });
  card.append(title, body, dots,
    el('div', { class: 'tour__acts' }, skip, back, next));
  root.append(...panes, ring, card);

  let at = 0;
  let stopped = false;

  const place = (): void => {
    const step = steps[at];
    if (!step) return;
    const node = step.target?.();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (!node) {
      // No target: a plain card in the middle, for openings and endings.
      for (const p of panes) p.style.cssText = '';
      panes[0]!.style.cssText = `inset:0`;
      panes[1]!.style.cssText = panes[2]!.style.cssText = panes[3]!.style.cssText = 'display:none';
      ring.style.display = 'none';
      card.style.left = `${Math.max(12, (vw - card.offsetWidth) / 2)}px`;
      card.style.top = `${Math.max(12, (vh - card.offsetHeight) / 2)}px`;
      return;
    }

    const r = node.getBoundingClientRect();
    const x = Math.max(0, r.left - RING);
    const y = Math.max(0, r.top - RING);
    const w = Math.min(vw - x, r.width + RING * 2);
    const h = Math.min(vh - y, r.height + RING * 2);

    ring.style.display = '';
    ring.style.cssText += `;left:${x}px;top:${y}px;width:${w}px;height:${h}px`;
    panes[0]!.style.cssText = `left:0;top:0;width:${vw}px;height:${y}px`;
    panes[1]!.style.cssText = `left:${x + w}px;top:${y}px;width:${Math.max(0, vw - x - w)}px;height:${h}px`;
    panes[2]!.style.cssText = `left:0;top:${y + h}px;width:${vw}px;height:${Math.max(0, vh - y - h)}px`;
    panes[3]!.style.cssText = `left:0;top:${y}px;width:${x}px;height:${h}px`;

    // Put the card wherever there is most room, then keep it on screen. A
    // callout that hangs off the edge of a phone is worse than no callout.
    const cw = card.offsetWidth;
    const ch = card.offsetHeight;
    const below = vh - (y + h) - GAP;
    const above = y - GAP;
    const top = below >= ch || below >= above
      ? Math.min(vh - ch - 12, y + h + GAP)
      : Math.max(12, y - ch - GAP);
    card.style.top = `${Math.max(12, top)}px`;
    card.style.left = `${Math.min(Math.max(12, r.left + r.width / 2 - cw / 2), vw - cw - 12)}px`;
  };

  const show = (): void => {
    const step = steps[at];
    if (!step) { finish(true); return; }
    step.before?.();
    title.textContent = step.title;
    body.textContent = step.body;
    clear(dots);
    steps.forEach((_, i) => dots.append(el('span', {
      class: 'tour__dot' + (i === at ? ' tour__dot--on' : ''),
    })));
    back.disabled = at === 0;
    next.textContent = at === steps.length - 1 ? 'Got it' : 'Next';
    skip.hidden = at === steps.length - 1;
    // Two frames: one for the text to lay out, one to measure it.
    requestAnimationFrame(() => requestAnimationFrame(place));
  };

  const go = (d: number): void => {
    steps[at]?.after?.();
    at = Math.min(steps.length, Math.max(0, at + d));
    if (at >= steps.length) { finish(true); return; }
    show();
  };

  function finish(completed: boolean): void {
    if (stopped) return;
    stopped = true;
    steps[at]?.after?.();
    window.removeEventListener('resize', place);
    window.removeEventListener('keydown', onKey, true);
    document.removeEventListener('pointerdown', onOutside, true);
    root.classList.add('tour--out');
    setTimeout(() => root.remove(), 180);
    opts.onDone?.(completed);
  }

  function onKey(ev: KeyboardEvent): void {
    if (ev.key === 'Escape') { ev.preventDefault(); finish(false); }
    else if (ev.key === 'ArrowRight' || ev.key === 'Enter') { ev.preventDefault(); go(1); }
    else if (ev.key === 'ArrowLeft') { ev.preventDefault(); go(-1); }
  }

  on(next, 'click', () => go(1));
  on(back, 'click', () => go(-1));
  on(skip, 'click', () => finish(false));
  /*
   * A tap anywhere outside the card closes the whole walkthrough.
   *
   * It used to move on to the next step, on the theory that people tap to
   * continue. On a phone the effect was a walkthrough you could not get out
   * of by touching away — every tap just produced the next callout. Next and
   * Back are right there on the card for anyone who wants to carry on.
   *
   * Listened for on the document, not the dim panes: the highlighted element
   * sits in an undimmed hole, and a touch there is a touch away too.
   */
  const onOutside = (e: PointerEvent): void => {
    if (e.target instanceof Node && card.contains(e.target)) return;
    finish(false);
  };
  document.addEventListener('pointerdown', onOutside, true);

  window.addEventListener('resize', place);
  window.addEventListener('keydown', onKey, true);
  document.body.append(root);
  show();
  next.focus();

  return { stop: () => finish(false) };
}

/**
 * What to say about each part of the game screen.
 *
 * Selectors rather than element references, so this module knows nothing about
 * how the game screen is built and cannot hold a stale node. Anything not on
 * screen — the score, which is a pre-game option — drops out of the list
 * rather than being explained to somebody who cannot see it.
 */
export function gameTourSteps(): TourStep[] {
  const q = (sel: string) => () => document.querySelector(sel);
  const steps: TourStep[] = [
    {
      title: 'These are your letters',
      body: 'Seven of them, all yours, and nobody else can see them. Drag one up ' +
            'onto the board to place it — or tap an empty square and then tap ' +
            'letters, which is easier on a phone.',
      target: q('.rack__tiles'),
    },
    {
      title: 'The board is yours alone',
      body: 'Build your own crossword on it. Words read across and down, ' +
            'everything has to join up, and two words that touch in a line are ' +
            'read as one — so leave a gap between them.',
      target: q('.board'),
    },
    {
      title: 'Shuffle them about',
      body: 'Reorders your rack and nothing else. It changes nothing in the ' +
            'game; it is for shaking a word loose when you have stared at the ' +
            'same seven letters for too long.',
      target: q('.rack__actions .btn'),
    },
    {
      title: 'Stuck? Say so',
      body: 'Sometimes letters simply cannot be played. Press this and everyone ' +
            'is asked. If every player agrees, the whole table draws fresh ' +
            'letters and play carries on — so nobody is ever truly stuck.',
      target: q('.stuckbtn'),
    },
    {
      title: 'This is the one that matters',
      body: 'The moment your rack is empty and every word is real, press it. ' +
            'Everyone at the table — you included — draws more letters and play ' +
            'carries straight on. Press it too early and you wait a few seconds.',
      target: q('.callbtn'),
    },
    {
      title: 'Ask whether a word is real',
      body: 'This asks the word list. You only get a few per match on purpose: ' +
            'with unlimited checks the game turns into typing letters until ' +
            'something goes green.',
      target: q('.topbar .btn.btn--sm'),
    },
    {
      title: 'How the bag is going',
      body: 'Letters left in the bag, and how many more are coming to you. When ' +
            'the bag can no longer go round the table, the next player to use ' +
            'every letter ends the match.',
      target: q('.topbar__stats'),
    },
    {
      title: 'Who else is here',
      body: 'Everybody at the table, how many letters they are still holding, ' +
            'and what has just happened. Tap a name to watch their grid being ' +
            'built.',
      target: q('button[aria-label="Players and activity"]'),
    },
    {
      title: 'Moving around, and picking letters up',
      body: 'Drag empty board to move it. Hold still on empty board for a second ' +
            'and you draw instead: loop round exactly the letters you want, any ' +
            'shape, then drag any one of them to move the lot. The loop button ' +
            'here draws without the wait; the dotted square draws a rectangle. ' +
            'Two fingers zoom.',
      target: q('.toolbar'),
    },
    {
      title: 'Themes, sound and size',
      body: 'Backgrounds, light or dark, tile size, and four sound atmospheres ' +
            'you can mute. Nothing here changes the rules.',
      target: q('button[aria-label="Settings"]'),
    },
    {
      title: 'Leaving',
      body: 'This one gets you out: leave the table, stop playing but keep ' +
            'watching, or — if you are the host — stop the match for everybody.',
      target: q('button[aria-label="Leave or end the match"]'),
    },
    {
      title: 'That is the whole game',
      body: 'Make words, use up your letters, call it. The ? button brings this ' +
            'back any time, and there is a full demonstration behind it too.',
    },
  ];
  // A step pointing at nothing is worse than one fewer step.
  return steps.filter((s) => !s.target || s.target());
}
